/**
 * BrokRay — the Brok avatar / "working" indicator: the official Brok Mantaray brand
 * mark that swims while Brok is thinking, and rests static otherwise (Story UI-U10 AC5).
 *
 * The geometry is the canonical mantaray SHAPE (path 2 of assets/mantaray.svg,
 * viewBox 0 0 460 460) on a TRANSPARENT background — the navy brand tile (path 1) is
 * intentionally dropped so the mark sits on any surface — filled with brand periwinkle
 * (#9FA5FF).
 *   - The swim is CSS drift + wing-flap (keyframes in the component-owned
 *     `brok-ray.css`; `index.css` is untouched — the U1 token story owns it).
 *   - An organic edge wobble via `feTurbulence` + `feDisplacementMap`, whose nodes
 *     are present in the DOM ONLY while animating (jsdom never renders them).
 *   - The filter id is `useId`-scoped so multiple rays on one page never collide.
 *
 * Motion gate: only a real browser that does NOT prefer reduced motion animates.
 * jsdom has no `matchMedia` → treated as reduced motion → fully static (no filter
 * node, no keyframe class) → deterministic tests; the swim is browser-only.
 *
 * [Source: apps/web/src/features/brok/assets/mantaray.svg] (official brand mark, path 2)
 * [Source: docs/design/2026-07-10_sebastian-package/design.md §6 motion]
 * [Source: docs/stories/ui-u10-brok-briefing-ray.md AC5]
 */
import { useId } from 'react';
import { cn } from '../../../lib/cn';
import './brok-ray.css';

export interface BrokRayProps {
  /** Swim when true AND the environment allows motion; otherwise rest static. */
  animated?: boolean;
  /** Width in px (the mark is square). Default 24. */
  size?: number;
  /** Accessible label → `role="img"`. Omitted → decorative (`aria-hidden`). */
  label?: string;
  className?: string;
}

/**
 * Motion is allowed only in a real browser without a reduced-motion preference.
 * jsdom (tests) has no `matchMedia` → returns false → the ray renders static.
 */
export function brokRayMotionAllowed(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    !window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

/** Brand periwinkle — the Mantaray fill (matches assets/mantaray.svg .cls-2). */
const MANTARAY_FILL = '#9FA5FF';

/** Official Mantaray SHAPE (path 2 of assets/mantaray.svg, viewBox 0 0 460 460). */
const RAY_BODY =
  'M366.41,210.44v-.05c-44.61-23.61-54.73-35.61-70-84.69l-.02.14c-4.54-12.45-21.31-15.36-29.58-4.9-26.01,40.28-47.46,68.98-94.7,80.1-27.42,5.92-45.3,10.43-72.73,16.21-1.09.23-2.92.66-12.81,2.27-2.96.48-5.08,2.96-4.98,5.46.09,2.25,1.97,4.4,4.63,4.98,25.9,6.71,51.8,13.41,77.7,20.12,26.4,6.84,47.23,24.63,56.68,49.53.06-.02,8.95,25.48,9.32,26.46v-.06c4.69,13.36,22.89,15.81,30.76,4v.06c32.48-50.51,47.43-64.15,103.07-88.04,12.91-5.34,14.5-24.07,2.67-31.57Z';

export function BrokRay({ animated = false, size = 24, label, className }: BrokRayProps) {
  const rawId = useId();
  // useId can contain ':' which is invalid in a bare SVG id / url() reference.
  const filterId = `brok-ray-${rawId.replace(/:/g, '')}`;
  const swim = animated && brokRayMotionAllowed();

  const a11y = label
    ? ({ role: 'img', 'aria-label': label } as const)
    : ({ 'aria-hidden': true } as const);

  return (
    <span
      className={cn('brok-ray inline-flex', swim && 'brok-ray--swim', className)}
      data-animated={swim ? 'true' : 'false'}
      {...a11y}
    >
      <svg
        width={size}
        height={size}
        viewBox="0 0 460 460"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        {/* Displacement wobble — only in the DOM while swimming. */}
        {swim ? (
          <filter id={filterId} x="-20%" y="-20%" width="140%" height="140%">
            <feTurbulence
              type="turbulence"
              baseFrequency="0.004 0.008"
              numOctaves="1"
              seed="7"
              result="noise"
            />
            <feDisplacementMap
              in="SourceGraphic"
              in2="noise"
              scale="14"
              xChannelSelector="R"
              yChannelSelector="G"
            />
          </filter>
        ) : null}
        <g
          className={swim ? 'brok-ray__body' : undefined}
          filter={swim ? `url(#${filterId})` : undefined}
        >
          {/* Mantaray shape only (transparent tile) — brand periwinkle. */}
          <path d={RAY_BODY} fill={MANTARAY_FILL} />
        </g>
      </svg>
    </span>
  );
}
